import { IContext } from "@src/context";
import {
  parseDateFromString,
  apartmentVisitsText,
  availableFromText,
} from "@src/utils/availableDate";
import { isFavorite, toggleFavorite } from "@src/utils/favorite";
import { isMobile } from "@src/utils/matchMedia";
import { getDate } from "@src/utils/date";

import messages from "./messages";
import "./index.css";

enum ESortBy {
  lowRent,
  highRent,
  smallSize,
  largeSize,
}

export function setupRooms(context: IContext) {
  const container = document.querySelector(".container-rooms");

  if (container == null) {
    return;
  }

  const { intl } = context;

  // scroll to room by hash
  scrollToRoomByHash();
  document.querySelectorAll("[data-anchor-room]").forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const hash = link.getAttribute("data-anchor-room");
      if (hash) {
        window.location.hash = "#" + hash;
        scrollToRoomByHash();
      }
    });
  });

  container
    .querySelectorAll(".container-rooms-group-card-list-item")
    .forEach((item) => {
      // set apartment visits and available from text
      const textWrap = item?.querySelector(
        ".container-rooms-group-card-list-item-detail-action-text"
      );
      if (textWrap) {
        const apartmentVisits = apartmentVisitsText(
          context,
          parseDateFromString(item.getAttribute("data-apartment-visits") || "")
        );
        const availableFrom = availableFromText(
          context,
          parseDateFromString(
            item.getAttribute("data-move-in-available-date") || ""
          )
        );
        if (apartmentVisits) {
          textWrap.insertAdjacentHTML(
            "beforeend",
            `<span>${intl.formatMessage(
              messages.apartmentVisitsLabel
            )} ${apartmentVisits}</span>`
          );
        }
        if (availableFrom) {
          textWrap.insertAdjacentHTML(
            "beforeend",
            `<span>${intl.formatMessage(
              messages.availableFromLabel
            )} ${availableFrom}</span>`
          );
        }
      }
      // set favorite
      const propertyId = item?.getAttribute("data-property-id");
      if (propertyId) {
        item.setAttribute("data-favorite", isFavorite(propertyId).toString());
      }
    });

  // PDP: rooms contact and favorite
  const contactAndFavorite = document.querySelector(
    ".container-rooms-contact-and-favorite"
  );
  const contactAndFavoritePropertyId =
    contactAndFavorite?.getAttribute("data-property-id");
  if (contactAndFavoritePropertyId) {
    contactAndFavorite?.setAttribute(
      "data-favorite",
      isFavorite(contactAndFavoritePropertyId).toString()
    );
  }
  contactAndFavorite
    ?.querySelector(".container-rooms-favorite")
    ?.addEventListener("click", () => {
      toggleFavorite(contactAndFavorite);
    });

  // append information date to detail section
  const informationDateText = `<p class="container-rooms-note">${intl.formatMessage(
    messages.informationDate,
    getDate(context, new Date())
  )}</p>`;
  container
    ?.querySelector(".container-rooms-group")
    ?.insertAdjacentHTML("afterend", informationDateText);

  // open room detail button
  document.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const button = event.target.closest(
        ".container-gallery-section-button[data-scroll-to-name]"
      );
      if (button) {
        const name = button.getAttribute("data-scroll-to-name");
        if (name) {
          const card = container.querySelector(
            `.container-rooms-group-card[data-name="${name}"]`
          );
          if (card) {
            closeAllCard();
            card.removeAttribute("data-closed");
            window.scrollTo({
              top: (card as HTMLElement)?.offsetTop,
              behavior: "smooth",
            });
          }
        }
      }
    }
  });

  // sorting rooms when change sort select
  document
    .querySelector("#sortPDP")
    ?.addEventListener("change", function (this: HTMLSelectElement) {
      const sortBy = Number(this.value);
      sortRooms(sortBy);
    });

  container.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      // toggle rooms group card
      if (event.target.closest(".container-rooms-group-card-header")) {
        event.target
          .closest(".container-rooms-group-card")
          ?.toggleAttribute("data-closed");
      } else if (
        event.target.closest(
          ".container-rooms-group-card-list-item-collapse"
        ) ||
        event.target.closest(
          ".container-rooms-group-card-list-item-detail-close"
        ) ||
        (isMobile() &&
          event.target.closest(
            ".container-rooms-group-card-list-item-section"
          ) &&
          !event.target.closest("[data-popup-id]") &&
          !event.target.closest(".container-rooms-favorite"))
      ) {
        // toggle rooms card detail
        const item = event.target.closest(
          ".container-rooms-group-card-list-item"
        );
        item?.toggleAttribute("data-detail") ||
          window.scrollTo({
            top: (item as HTMLElement)?.offsetTop,
            behavior: "smooth",
          });
      } else if (event.target.closest(".container-rooms-favorite")) {
        // toggle rooms favorite
        const item = event.target.closest(
          ".container-rooms-group-card-list-item"
        );
        if (item) {
          toggleFavorite(item);
        }
      } else if (event.target.closest("div[data-popup-id=room-maisoku]")) {
        const button = event.target.closest<HTMLDivElement>(
          "div[data-popup-id=room-maisoku]"
        );
        const url = button?.getAttribute("data-iframe-url") || "";
        const iframe = document.createElement("iframe");
        iframe.setAttribute("src", url);
        iframe.setAttribute("border", "0");
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("width", "100%");
        iframe.setAttribute("height", "100%");
        document
          .querySelector<HTMLDivElement>(".container-maisoku-content")
          ?.replaceChildren(iframe);
      }
    }
  });

  function sortRooms(sortBy: number) {
    const groupList = document.querySelectorAll(
      ".container-rooms-group-card"
    ) as NodeListOf<HTMLDivElement>;
    const roomList = document.querySelectorAll(
      ".container-rooms-group-card-list-item"
    ) as NodeListOf<HTMLDivElement>;
    sort(sortBy, groupList);
    sort(sortBy, roomList);
  }

  function sort(sortBy: number, groupList: NodeListOf<HTMLDivElement>) {
    if (isNaN(sortBy) || typeof sortBy !== "number") return;

    const length = groupList.length;
    if (!length || length <= 1) {
      groupList.item(0)?.removeAttribute("data-closed");
      return;
    }

    const idxArray = Array.from({ length }, (_, index) => index);

    idxArray.sort((idxA, idxB) => {
      const { rent, size, disabled } = groupList.item(idxA).dataset;
      const {
        rent: rentB,
        size: sizeB,
        disabled: disabledB,
      } = groupList.item(idxB).dataset;
      // disable item always at the last
      if (disabled !== undefined) return 1;
      if (disabledB !== undefined) return -1;
      switch (sortBy) {
        case ESortBy.lowRent:
          return Number(rent) - Number(rentB);
        case ESortBy.highRent:
          return Number(rentB) - Number(rent);
        case ESortBy.smallSize:
          return Number(size) - Number(sizeB);
        case ESortBy.largeSize:
          return Number(sizeB) - Number(size);
      }
      return 0;
    });
    idxArray.forEach((key, position) => {
      groupList.item(key).style.order = String(position + 1);
      if (position === 0) {
        groupList.item(key).removeAttribute("data-closed");
      } else {
        groupList.item(key).setAttribute("data-closed", "");
      }
    });
  }

  function closeAllCard() {
    container
      ?.querySelectorAll(".container-rooms-group-card")
      .forEach((card) => {
        card.setAttribute("data-closed", "");
      });
  }

  // scroll to anchor by hash
  function scrollToRoomByHash() {
    const hash = window.location.hash;
    if (hash) {
      const [roomId, ...rest] = hash.replace("#", "").split("-");
      const cardList = container?.querySelector(
        `.container-rooms-group-card[data-name="${roomId}"]`
      );
      const cardItem = cardList?.querySelector(
        `.container-rooms-group-card-list-item[data-room="${rest.join("-")}"]`
      );
      if (cardList) {
        closeAllCard();
        cardList.removeAttribute("data-closed");
        window.scrollTo({
          top: ((cardItem || cardList) as HTMLElement)?.offsetTop,
          behavior: "smooth",
        });
      }
    }
  }
}
